import jwt_decode from 'jwt-decode'

const REFRESH_THRESHOLD_SECONDS = 10 * 60

export const SESSION_EXPIRED_EVENT = 'wizr-session-expired'

const isWizrBaseUrl = process.env.REACT_APP_API_BASE_URL === 'https://apitwo.wizrai.com/'

// Use sessionStorage for cross-module state sharing
const SESSION_EXPIRED_FLAG = 'wizr_session_expired'

export const isSessionExpired = (): boolean => {
  return sessionStorage.getItem(SESSION_EXPIRED_FLAG) === 'true'
}

export const resetSessionExpiry = (): void => {
  sessionStorage.removeItem(SESSION_EXPIRED_FLAG)
}

export const dispatchSessionExpired = () => {
  if (isWizrBaseUrl && !sessionStorage.getItem('pkce_in_progress')) {
    // Only dispatch once to prevent duplicate modals and notifications
    if (isSessionExpired()) {
      return
    }
    sessionStorage.setItem(SESSION_EXPIRED_FLAG, 'true')
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT))
  }
}

let pendingRefresh: Promise<string> | null = null

// Helper to check if OAuth is in progress
const isOAuthInProgress = (): boolean => {
  return Boolean(sessionStorage.getItem('pkce_in_progress') || new URLSearchParams(window.location.search).get('code'))
}

export const getValidToken = async (): Promise<string | null> => {
  const token = localStorage.getItem('id_token')
  if (!token) return null

  // If OAuth is in progress, wait for it to complete first
  if (isOAuthInProgress()) {
    // Return null to prevent API calls during OAuth flow
    return null
  }

  try {
    const decoded: any = jwt_decode(token)
    if (!decoded.exp) return token

    const nowInSeconds = Math.floor(Date.now() / 1000)
    const secondsUntilExpiry = decoded.exp - nowInSeconds

    // Already expired — show session expired modal
    if (secondsUntilExpiry <= 0) {
      dispatchSessionExpired()
      return token
    }

    // More than 10 min remaining — no refresh needed
    if (secondsUntilExpiry > REFRESH_THRESHOLD_SECONDS) return token

    // Within 10 min of expiry AND token has been alive long enough — proactively refresh once
    if (!pendingRefresh) {
      pendingRefresh = doRefresh().finally(() => { pendingRefresh = null })
    }
    return pendingRefresh
  } catch {
    return token
  }
}

const doRefresh = async (): Promise<string> => {
  const currentToken = localStorage.getItem('id_token')!
  const refreshToken = localStorage.getItem('refresh_token')

  if (!refreshToken) throw new Error('No refresh token in storage')

  const apiKey = process.env.REACT_APP_API_AUTHORIZATION_HEADER_APIKEY
  const apiKeyValue = process.env.REACT_APP_API_AUTHORIZATION_HEADER_APIKEY_VALUE
  const url = process.env.REACT_APP_REFRESH_TOKEN_URL!

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'wizr-auth': currentToken,
  }
  if (apiKey && apiKeyValue) {
    headers[apiKey] = apiKeyValue
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ refresh_token: refreshToken }),
  })

  if (!response.ok) throw new Error(`Token refresh failed: ${response.status}`)

  const json = await response.json()
  const newToken: string = json.data.token
  const newRefresh: string = json.data.refresh_token

  localStorage.setItem('id_token', newToken)
  if (newRefresh) localStorage.setItem('refresh_token', newRefresh)

  // Reset session expiry flag on successful token refresh
  resetSessionExpiry()

  return newToken
}
